import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, UserPlus, Search, Volume2, Video, Music, Calendar, X, Check } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { uploadAudio, uploadVideo, validateFile } from '../utils/cloudinaryUpload';
import '../styles/StoryCreationPage.css';

const StoryCreationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        setUser(authUser);
      } catch (error) {
        console.error('Error checking auth:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  const searchParams = new URLSearchParams(location.search);
  const initialArtworkId = searchParams.get('artworkId') || '';
  const initialEventIdParam = searchParams.get('eventId');
  const parsedEventId = initialEventIdParam && !Number.isNaN(Number(initialEventIdParam))
    ? Number(initialEventIdParam)
    : null;

  const [formData, setFormData] = useState({
    title: '',
    story_text: '',
    story_type: 'meaning',
    vc_id: initialArtworkId,
    event_id: parsedEventId,
    artist_uuid: '',
    featured: false,
    audio_url: '',
    video_url: ''
  });

  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [artworkSearch, setArtworkSearch] = useState('');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [writers, setWriters] = useState([]);
  const [writerSearch, setWriterSearch] = useState('');
  const [filteredWriters, setFilteredWriters] = useState([]);
  const [selectedWriter, setSelectedWriter] = useState(null);
  const [showWriterForm, setShowWriterForm] = useState(false);
  const [newWriter, setNewWriter] = useState({
    first_name: '',
    last_name: '',
    email: '',
    nickname: '',
    mobile_phone: ''
  });
  const [searchingWriters, setSearchingWriters] = useState(false);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [creatingWriter, setCreatingWriter] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');

  const audioFileRef = useRef(null);
  const videoFileRef = useRef(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      vc_id: initialArtworkId || prev.vc_id,
      event_id: parsedEventId
    }));
  }, [initialArtworkId, parsedEventId]);

  // Live search writers from database when typing
  useEffect(() => {
    const searchWritersInDb = async () => {
      if (!writerSearch || writerSearch.length < 2) {
        setFilteredWriters([]);
        return;
      }

      setSearchingWriters(true);
      try {
        const searchValue = writerSearch.toLowerCase();
        const { data, error } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname, email')
          .or(`first_name.ilike.%${searchValue}%,last_name.ilike.%${searchValue}%,nickname.ilike.%${searchValue}%,email.ilike.%${searchValue}%`)
          .order('first_name')
          .limit(20);

        if (error) {
          console.error('Error searching writers:', error);
          setFilteredWriters([]);
        } else {
          setFilteredWriters(data || []);
        }
      } catch (error) {
        console.error('Error searching writers:', error);
        setFilteredWriters([]);
      } finally {
        setSearchingWriters(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(searchWritersInDb, 300);
    return () => clearTimeout(timeoutId);
  }, [writerSearch]);

  useEffect(() => {
    if (!artworkSearch) {
      setFilteredArtworks(artworks);
      return;
    }

    const searchValue = artworkSearch.toLowerCase();
    const filtered = artworks.filter((artwork) => {
      const titleMatch = (artwork.title || '').toLowerCase().includes(searchValue);
      const artistName = artwork.visual_artist
        ? `${artwork.visual_artist.first_name || ''} ${artwork.visual_artist.last_name || ''} ${artwork.visual_artist.nickname || ''}`
            .toLowerCase()
        : '';
      return titleMatch || artistName.includes(searchValue);
    });

    setFilteredArtworks(filtered);
  }, [artworkSearch, artworks]);

  useEffect(() => {
    if (!eventSearch) {
      setFilteredEvents(events);
      return;
    }

    const searchValue = eventSearch.toLowerCase();
    const filtered = events.filter((event) => {
      const nameMatch = (event.name || '').toLowerCase().includes(searchValue);
      return nameMatch;
    });

    setFilteredEvents(filtered);
  }, [eventSearch, events]);

  // Pre-populate artwork when artworkId is in URL
  useEffect(() => {
    if (initialArtworkId && artworks.length > 0) {
      const artwork = artworks.find(a => String(a.vc_id) === String(initialArtworkId));
      console.log('Looking for artwork:', initialArtworkId, 'Found:', artwork);
      if (artwork && !selectedArtwork) {
        console.log('Setting selected artwork:', artwork);
        setSelectedArtwork(artwork);
        setArtworkSearch(artwork.title);
        setFormData(prev => ({ ...prev, vc_id: artwork.vc_id }));
      }
    }
  }, [initialArtworkId, artworks]);

  // Pre-populate event when eventId is in URL
  useEffect(() => {
    if (parsedEventId && events.length > 0) {
      const event = events.find(e => e.id === parsedEventId);
      if (event && !selectedEvent) {
        setSelectedEvent(event);
        setEventSearch(event.name);
        setFormData(prev => ({ ...prev, event_id: event.id }));
      }
    }
  }, [parsedEventId, events]);

  const formatEventLabel = (event) => {
    if (!event) return 'Untitled Event';
    const displayDate = event.event_start
      ? new Date(event.event_start).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : null;
    return `${event.name || 'Untitled Event'}${displayDate ? ` — ${displayDate}` : ''}`;
  };

  const loadData = useCallback(async () => {
    if (!user && !authLoading) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      // If artworkId is provided, fetch it specifically first
      let specificArtwork = null;
      if (initialArtworkId) {
        const { data: artworkData, error: artworkError } = await supabase
          .from('vcs')
          .select('vc_id, title, cover_image, uuid')
          .eq('vc_id', initialArtworkId)
          .eq('access_control', 'Public')
          .not('title', 'is', null)
          .single();

        if (artworkError) {
          console.error('Error fetching specific artwork:', artworkError);
        } else if (artworkData) {
          // Fetch artist separately
          let artistData = null;
          if (artworkData.uuid) {
            const { data: artist } = await supabase
              .from('users')
              .select('uuid, first_name, last_name, nickname')
              .eq('uuid', artworkData.uuid)
              .single();
            artistData = artist;
          }

          specificArtwork = {
            vc_id: artworkData.vc_id,
            title: artworkData.title || 'Untitled',
            cover_image: artworkData.cover_image,
            visual_artist: artistData || null
          };
          console.log('Fetched specific artwork:', specificArtwork);
        }
      }

      const [artworkResponse, writerResponse, eventsResponse] = await Promise.all([
        supabase
          .from('vcs')
          .select('vc_id, title, cover_image, uuid')
          .eq('access_control', 'Public')
          .not('title', 'is', null)
          .order('title', { ascending: true })
          .limit(500),
        supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname, email')
          .order('first_name'),
        supabase
          .from('events')
          .select('id, name, event_start')
          .order('event_start', { ascending: false })
          .limit(200)
      ]);

      let processedArtworks = [];
      if (artworkResponse.error) {
        console.error('Error loading artworks:', artworkResponse.error);
      } else {
        const artworksData = artworkResponse.data || [];
        
        // Get all unique artist UUIDs
        const artistUuids = [...new Set(artworksData.map(a => a.uuid).filter(Boolean))];
        
        // Fetch all artists in one query
        let artistMap = new Map();
        if (artistUuids.length > 0) {
          const { data: artists } = await supabase
            .from('users')
            .select('uuid, first_name, last_name, nickname')
            .in('uuid', artistUuids);
          
          if (artists) {
            artists.forEach(artist => {
              artistMap.set(artist.uuid, artist);
            });
          }
        }
        
        // Map artworks with their artists
        processedArtworks = artworksData.map((item) => ({
          vc_id: item.vc_id,
          title: item.title || 'Untitled',
          cover_image: item.cover_image,
          visual_artist: item.uuid ? artistMap.get(item.uuid) || null : null
        }));
        
        // Add the specific artwork to the beginning if it was fetched and isn't already in the list
        if (specificArtwork && !processedArtworks.some(a => a.vc_id === specificArtwork.vc_id)) {
          processedArtworks.unshift(specificArtwork);
        }
        
        console.log('Loaded artworks:', processedArtworks.length, 'First few:', processedArtworks.slice(0, 3));
      }

      if (!processedArtworks.length) {
        const { data: fallbackArtworks, error: fallbackError } = await supabase
          .from('vcs')
          .select('vc_id, title, cover_image, artist_uuid')
          .order('title', { ascending: true })
          .limit(500);

        if (fallbackError) {
          console.error('Fallback artwork load failed:', fallbackError);
        } else {
          processedArtworks = (fallbackArtworks || []).map((item) => ({
            vc_id: item.vc_id,
            title: item.title,
            cover_image: item.cover_image,
            visual_artist: null,
            artist_uuid: item.artist_uuid || null
          }));

          const artistIds = [
            ...new Set(
              processedArtworks
                .map((item) => item.artist_uuid)
                .filter((id) => Boolean(id))
            )
          ];

          if (artistIds.length) {
            const { data: artistRecords, error: artistError } = await supabase
              .from('users')
              .select('uuid, first_name, last_name, nickname')
              .in('uuid', artistIds);

            if (artistError) {
              console.error('Fallback artist lookup failed:', artistError);
            } else {
              const artistMap = new Map(
                (artistRecords || []).map((artist) => [artist.uuid, artist])
              );
              processedArtworks = processedArtworks.map((artwork) => ({
                vc_id: artwork.vc_id,
                title: artwork.title,
                cover_image: artwork.cover_image,
                visual_artist: artistMap.get(artwork.artist_uuid) || null
              }));
            }
          } else {
            processedArtworks = processedArtworks.map((artwork) => ({
              vc_id: artwork.vc_id,
              title: artwork.title,
              cover_image: artwork.cover_image,
              visual_artist: null
            }));
          }
        }
      }

      setArtworks(processedArtworks);
      setFilteredArtworks(processedArtworks);
      setArtworkSearch('');

      if (writerResponse.error) {
        console.error('Error loading users:', writerResponse.error);
      }
      setWriters(writerResponse.data || []);
      setFilteredWriters(writerResponse.data || []);

      if (eventsResponse.error) {
        console.error('Error loading events:', eventsResponse.error);
        setEvents([]);
      } else {
        let loadedEvents = eventsResponse.data || [];

        if (
          parsedEventId &&
          !loadedEvents.some((event) => event.id === parsedEventId)
        ) {
          const { data: focusedEvent, error: focusedEventError } = await supabase
            .from('events')
            .select('id, name, event_start')
            .eq('id', parsedEventId)
            .single();

          if (!focusedEventError && focusedEvent) {
            loadedEvents = [focusedEvent, ...loadedEvents];
          }
        }

        const dedupedEvents = loadedEvents.reduce((accumulator, event) => {
          if (!accumulator.some((existing) => existing.id === event.id)) {
            accumulator.push(event);
          }
          return accumulator;
        }, []);

        setEvents(dedupedEvents);
        setFilteredEvents(dedupedEvents);
      }
    } catch (error) {
      console.error('Error loading story creation data:', error);
    } finally {
      setLoading(false);
    }
  }, [authLoading, navigate, parsedEventId, user]);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      loadData();
    }
  }, [authLoading, user, loadData, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (file, type) => {
    const validation = validateFile(file, type);
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    if (type === 'audio') {
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, audio_url: '' }));
    } else if (type === 'video') {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, video_url: '' }));
    }
  };

  const handleFileUpload = async (type) => {
    try {
      if (type === 'audio' && audioFile) {
        setUploadingAudio(true);
        console.log('Starting audio upload...', audioFile.name);
        const result = await uploadAudio(audioFile);
        console.log('Audio upload result:', result);
        setFormData((prev) => ({ ...prev, audio_url: result.url }));
        setAudioFile(null);
        setAudioPreview('');
        console.log('Audio URL set to:', result.url);
      } else if (type === 'video' && videoFile) {
        setUploadingVideo(true);
        console.log('Starting video upload...', videoFile.name);
        const result = await uploadVideo(videoFile);
        console.log('Video upload result:', result);
        setFormData((prev) => ({ ...prev, video_url: result.url }));
        setVideoFile(null);
        setVideoPreview('');
        console.log('Video URL set to:', result.url);
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      alert(`Failed to upload ${type}: ${error.message}`);
    } finally {
      setUploadingAudio(false);
      setUploadingVideo(false);
    }
  };

  const removeFile = (type) => {
    if (type === 'audio') {
      setAudioFile(null);
      setAudioPreview('');
      setFormData((prev) => ({ ...prev, audio_url: '' }));
    } else if (type === 'video') {
      setVideoFile(null);
      setVideoPreview('');
      setFormData((prev) => ({ ...prev, video_url: '' }));
    }
  };

  const handleWriterSelect = (writer) => {
    setFormData((prev) => ({ ...prev, artist_uuid: writer.uuid }));
    setSelectedWriter(writer);
    setWriterSearch('');
    setShowWriterForm(false);
  };

  // Format phone number to E.164 format for OTP verification
  const formatPhoneForOtp = (phone) => {
    if (!phone) return null;
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // If it's 10 digits, assume US and add +1
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    // If it starts with 1 and is 11 digits, add +
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    // If it already has country code (11+ digits), add +
    if (digits.length >= 11) {
      return `+${digits}`;
    }
    // Return as-is if unclear
    return phone;
  };

  const handleCreateWriter = async () => {
    if (!newWriter.first_name || !newWriter.last_name || !newWriter.email) {
      alert('Please fill in all required writer fields');
      return;
    }

    setCreatingWriter(true);
    try {
      const { data: existingUser } = await supabase
        .from('users')
        .select('uuid')
        .eq('email', newWriter.email)
        .single();

      if (existingUser) {
        alert('A user with this email already exists');
        return;
      }

      // Format phone for OTP verification compatibility
      const formattedPhone = formatPhoneForOtp(newWriter.mobile_phone);

      // Generate a UUID for the new user
      const newUuid = crypto.randomUUID();

      const { data: userData, error } = await supabase
        .from('users')
        .insert({
          uuid: newUuid,
          first_name: newWriter.first_name,
          last_name: newWriter.last_name,
          email: newWriter.email,
          nickname: newWriter.nickname || null,
          mobile_phone: formattedPhone,
          created_at: new Date().toISOString()
        })
        .select('uuid, first_name, last_name, nickname, email, mobile_phone')
        .single();

      if (error) throw error;

      setWriters((prev) => [userData, ...prev]);
      setFilteredWriters((prev) => [userData, ...prev]);
      handleWriterSelect(userData);

      setNewWriter({ first_name: '', last_name: '', email: '', nickname: '', mobile_phone: '' });
      setShowWriterForm(false);

      alert('Writer created successfully!');
    } catch (error) {
      console.error('Error creating writer:', error);
      alert('Failed to create writer: ' + error.message);
    } finally {
      setCreatingWriter(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.vc_id) {
      alert('Please select an artwork to link this story to.');
      return;
    }

    if (!formData.artist_uuid) {
      alert('Please select a story writer.');
      return;
    }

    setCreating(true);
    try {
      // Get current user session to verify authentication
      const { data: sessionData } = await supabase.auth.getSession();
      console.log('Current user session:', sessionData?.session?.user?.id);
      console.log('Story writer UUID:', formData.artist_uuid);

      const payload = {
        title: formData.title || null,
        story_text: formData.story_text || null,
        story_type: formData.story_type || null,
        vc_id: formData.vc_id,
        event_id: formData.event_id,
        artist_uuid: formData.artist_uuid, // Required field - story writer's UUID
        featured: formData.featured,
        performed_at_event: Boolean(formData.event_id),
        visibility: 'public',
        audio_url: formData.audio_url || null,
        video_url: formData.video_url || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        published_at: new Date().toISOString() // Publish immediately
      };

      console.log('Submitting payload:', payload);

      // First try insert without select to avoid SELECT policy issues
      const { data, error } = await supabase
        .from('artspeaks_stories')
        .insert(payload);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Insert successful:', data);
      alert('Story created successfully!');
      navigate('/artSpeaks');
    } catch (error) {
      console.error('Error creating story:', error);
      alert('Failed to create story: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="story-creation-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading story builder…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="story-creation-page">
        <div className="error-state">
          <h2>Sign in required</h2>
          <p>You need a curator account to create an Art Speaks story.</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/login')}
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="story-creation-page">
      <div className="page-header">
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1>Create Art Speaks Story</h1>
        <p className="page-subtitle">
          Story creation is flexible—only an artwork link is required. Add writers, audio, or events whenever they are ready.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="story-form">
        <div className="form-grid">
          <div className="form-column">
            <div className="form-section">
              <h3>Story Basics</h3>

              <div className="form-group">
                <label>Story Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Optional—name this story"
                />
              </div>

              <div className="form-group">
                <label>Story Type</label>
                <select
                  value={formData.story_type}
                  onChange={(e) => handleInputChange('story_type', e.target.value)}
                >
                  <option value="meaning">Meaning - What the work means</option>
                  <option value="inspiration">Inspiration - What inspired the work</option>
                  <option value="process">Process - How the work was created</option>
                  <option value="technique">Technique - Technical aspects</option>
                  <option value="personal">Personal - Personal connection to the work</option>
                  <option value="collaboration">Collaboration - Working with others</option>
                </select>
              </div>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleInputChange('featured', e.target.checked)}
                />
                Featured Story
              </label>
            </div>

            <div className="form-section">
              <h3>Story Writer</h3>

              {selectedWriter ? (
                <div className="selected-writer-panel">
                  <div className="selected-header">
                    <Check className="check-icon" size={16} />
                    <span>Selected Writer</span>
                  </div>
                  <div className="selected-writer-info">
                    <div className="writer-details">
                      <div className="writer-name-large">
                        {selectedWriter.first_name} {selectedWriter.last_name}
                        {selectedWriter.nickname && (
                          <span className="nickname"> ({selectedWriter.nickname})</span>
                        )}
                      </div>
                      <div className="writer-email-small">{selectedWriter.email}</div>
                    </div>
                    <button
                      type="button"
                      className="btn-text"
                      onClick={() => {
                        setSelectedWriter(null);
                        setFormData((prev) => ({ ...prev, artist_uuid: '' }));
                      }}
                    >
                      Change Writer
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Search Writer</label>
                    <div className="search-input-wrapper">
                      <Search className="search-icon" size={18} />
                      <input
                        type="text"
                        value={writerSearch}
                        onChange={(e) => setWriterSearch(e.target.value)}
                        placeholder="Type at least 2 characters to search..."
                        className="search-input"
                      />
                      {writerSearch && (
                        <button
                          type="button"
                          className="clear-search-btn"
                          onClick={() => setWriterSearch('')}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>

                    {writerSearch && writerSearch.length >= 2 && (
                      <div className="writer-dropdown">
                        {searchingWriters ? (
                          <div className="searching-writers">
                            Searching...
                          </div>
                        ) : filteredWriters.length > 0 ? (
                          filteredWriters.slice(0, 10).map((writer) => (
                            <button
                              key={writer.uuid}
                              type="button"
                              className="writer-option"
                              onClick={() => handleWriterSelect(writer)}
                            >
                              <div className="writer-info">
                                <span className="writer-name">
                                  {writer.first_name} {writer.last_name}
                                  {writer.nickname && ` (${writer.nickname})`}
                                </span>
                                <span className="writer-email">{writer.email}</span>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="no-writers">
                            No writers found.{' '}
                            <button type="button" onClick={() => setShowWriterForm(true)}>
                              Create new writer
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowWriterForm(true)}
                    className="btn-secondary"
                    style={{ marginTop: '0.5rem' }}
                  >
                    <UserPlus size={16} />
                    Create New Writer
                  </button>
                </>
              )}

              {showWriterForm && (
                <div className="new-writer-form">
                  <h4>Create New Writer</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={newWriter.first_name}
                        onChange={(e) =>
                          setNewWriter((prev) => ({ ...prev, first_name: e.target.value }))
                        }
                        placeholder="First name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        value={newWriter.last_name}
                        onChange={(e) =>
                          setNewWriter((prev) => ({ ...prev, last_name: e.target.value }))
                        }
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={newWriter.email}
                        onChange={(e) =>
                          setNewWriter((prev) => ({ ...prev, email: e.target.value }))
                        }
                        placeholder="Email address"
                      />
                    </div>
                    <div className="form-group">
                      <label>Public Name</label>
                      <input
                        type="text"
                        value={newWriter.nickname}
                        onChange={(e) =>
                          setNewWriter((prev) => ({ ...prev, nickname: e.target.value }))
                        }
                        placeholder="Display name (optional)"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Mobile Phone</label>
                      <input
                        type="tel"
                        value={newWriter.mobile_phone}
                        onChange={(e) =>
                          setNewWriter((prev) => ({ ...prev, mobile_phone: e.target.value }))
                        }
                        placeholder="(555) 123-4567"
                      />
                      <span className="field-hint">For OTP verification in mobile app</span>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={() => setShowWriterForm(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateWriter}
                      disabled={creatingWriter}
                      className="btn-primary"
                    >
                      {creatingWriter ? 'Creating...' : 'Create Writer'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Story Content</h3>

              <div className="form-group">
                <label>Story Text</label>
                <textarea
                  value={formData.story_text}
                  onChange={(e) => handleInputChange('story_text', e.target.value)}
                  placeholder="Drop in narration or notes whenever they are ready."
                  rows={8}
                />
              </div>
            </div>
          </div>

          <div className="form-column">
            <div className="form-section highlight">
              <h3>Artwork *</h3>

              <div className="form-group">
                <label>Search Artwork</label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Search style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    color: '#9ca3af',
                    pointerEvents: 'none',
                    zIndex: 1
                  }} />
                  <input
                    type="text"
                    value={artworkSearch}
                    onChange={(e) => {
                      setArtworkSearch(e.target.value);
                      if (!e.target.value) {
                        setSelectedArtwork(null);
                        handleInputChange('vc_id', '');
                      }
                    }}
                    placeholder="Search artworks by title..."
                    style={{
                      width: '100%',
                      paddingLeft: '40px',
                      paddingRight: '40px',
                      height: '40px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {artworkSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setArtworkSearch('');
                        setSelectedArtwork(null);
                        handleInputChange('vc_id', '');
                      }}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        zIndex: 1
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                
                {artworkSearch && filteredArtworks.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: '#fff'
                  }}>
                    {filteredArtworks.slice(0, 10).map((artwork) => (
                      <button
                        key={artwork.vc_id}
                        type="button"
                        onClick={() => {
                          setSelectedArtwork(artwork);
                          handleInputChange('vc_id', artwork.vc_id);
                          setArtworkSearch(artwork.title);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          textAlign: 'left',
                          border: 'none',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        {artwork.cover_image && (
                          <img
                            src={artwork.cover_image}
                            alt={artwork.title}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              flexShrink: 0
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {artwork.title}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {artwork.visual_artist
                              ? artwork.visual_artist.nickname ||
                                `${artwork.visual_artist.first_name} ${artwork.visual_artist.last_name}`
                              : 'Unknown Artist'}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {artworkSearch && filteredArtworks.length === 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}>
                    No artworks found
                  </div>
                )}
              </div>

              {selectedArtwork && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #0ea5e9',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {selectedArtwork.cover_image && (
                    <img
                      src={selectedArtwork.cover_image}
                      alt={selectedArtwork.title}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        flexShrink: 0
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', color: '#0284c7', marginBottom: '4px' }}>
                      ✓ Selected Artwork
                    </div>
                    <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                      {selectedArtwork.title}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#666' }}>
                      {selectedArtwork.visual_artist
                        ? selectedArtwork.visual_artist.nickname ||
                          `${selectedArtwork.visual_artist.first_name} ${selectedArtwork.visual_artist.last_name}`
                        : 'Unknown Artist'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Event (Optional)</h3>
              
              {selectedEvent && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Calendar size={20} style={{ color: '#d97706', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', color: '#d97706', marginBottom: '4px' }}>
                      ✓ Selected Event
                    </div>
                    <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                      {selectedEvent.name}
                    </div>
                    {selectedEvent.event_start && (
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        {new Date(selectedEvent.event_start).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Search Event</label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Calendar style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    color: '#9ca3af',
                    pointerEvents: 'none',
                    zIndex: 1
                  }} />
                  <input
                    type="text"
                    value={eventSearch}
                    onChange={(e) => {
                      setEventSearch(e.target.value);
                      if (!e.target.value) {
                        setSelectedEvent(null);
                        handleInputChange('event_id', null);
                      }
                    }}
                    placeholder="Search events by name..."
                    style={{
                      width: '100%',
                      paddingLeft: '40px',
                      paddingRight: '40px',
                      height: '40px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {eventSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setEventSearch('');
                        setSelectedEvent(null);
                        handleInputChange('event_id', null);
                      }}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        zIndex: 1
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                
                {eventSearch && filteredEvents.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    maxHeight: '250px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: '#fff'
                  }}>
                    {filteredEvents.slice(0, 10).map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => {
                          setSelectedEvent(event);
                          handleInputChange('event_id', event.id);
                          setEventSearch(event.name);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          textAlign: 'left',
                          border: 'none',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '2px' }}>
                          {event.name}
                        </div>
                        {event.event_start && (
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {new Date(event.event_start).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {eventSearch && filteredEvents.length === 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}>
                    No events found
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>Media (Optional)</h3>

              <div className="form-group">
                <label>Audio Story</label>
                
                {formData.audio_url ? (
                  <div className="uploaded-media-panel">
                    <div className="uploaded-header">
                      <Check className="check-icon" size={16} />
                      <span>Audio Uploaded</span>
                    </div>
                    <audio controls className="audio-preview">
                      <source src={formData.audio_url} />
                    </audio>
                    <button
                      type="button"
                      onClick={() => removeFile('audio')}
                      className="btn-text"
                      style={{ marginTop: '0.5rem' }}
                    >
                      Remove Audio
                    </button>
                  </div>
                ) : (
                  <div className="media-upload-container">
                    <input
                      ref={audioFileRef}
                      type="file"
                      accept="audio/*"
                      onChange={(e) => handleFileSelect(e.target.files[0], 'audio')}
                      style={{ display: 'none' }}
                    />

                    {!audioFile ? (
                      <div className="upload-area">
                        <button
                          type="button"
                          onClick={() => audioFileRef.current?.click()}
                          className="upload-btn"
                        >
                          <Music className="w-4 h-4" />
                          Choose Audio File
                        </button>
                        <span className="upload-hint">MP3, WAV, M4A, AAC up to 50MB</span>
                      </div>
                    ) : (
                      <>
                        <div className="file-preview">
                          <div className="file-info">
                            <Volume2 className="w-4 h-4" />
                            <span>{audioFile.name}</span>
                          </div>
                          <div className="file-actions">
                            <button
                              type="button"
                              onClick={() => handleFileUpload('audio')}
                              disabled={uploadingAudio}
                              className="btn-primary btn-sm"
                            >
                              {uploadingAudio ? 'Uploading...' : 'Upload to Cloudinary'}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFile('audio')}
                              className="btn-secondary btn-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        {audioPreview && (
                          <audio controls className="audio-preview">
                            <source src={audioPreview} />
                          </audio>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Video Story</label>
                <div className="media-upload-container">
                  <input
                    ref={videoFileRef}
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileSelect(e.target.files[0], 'video')}
                    style={{ display: 'none' }}
                  />

                  {!videoFile && !formData.video_url && (
                    <div className="upload-area">
                      <button
                        type="button"
                        onClick={() => videoFileRef.current?.click()}
                        className="upload-btn"
                      >
                        <Video className="w-4 h-4" />
                        Choose Video File
                      </button>
                      <span className="upload-hint">MP4, MOV, AVI up to 100MB</span>
                    </div>
                  )}

                  {videoFile && (
                    <div className="file-preview">
                      <div className="file-info">
                        <Video className="w-4 h-4" />
                        <span>{videoFile.name}</span>
                      </div>
                      <div className="file-actions">
                        <button
                          type="button"
                          onClick={() => handleFileUpload('video')}
                          disabled={uploadingVideo}
                          className="btn-primary btn-sm"
                        >
                          {uploadingVideo ? 'Uploading...' : 'Upload'}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFile('video')}
                          className="btn-secondary btn-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  {videoPreview && (
                    <video controls className="video-preview">
                      <source src={videoPreview} />
                    </video>
                  )}

                  {formData.video_url && !videoFile && (
                    <div className="uploaded-media-preview">
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f0f9ff', 
                        border: '1px solid #7dd3fc', 
                        borderRadius: '6px',
                        marginTop: '8px'
                      }}>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          color: '#0284c7', 
                          marginBottom: '8px',
                          fontWeight: '500'
                        }}>
                          ✓ Video uploaded successfully
                        </div>
                        <video controls className="video-preview" style={{ width: '100%', maxHeight: '300px' }}>
                          <source src={formData.video_url} />
                        </video>
                        <button
                          type="button"
                          onClick={() => handleInputChange('video_url', '')}
                          className="btn-secondary btn-sm"
                          style={{ marginTop: '8px' }}
                        >
                          Remove Video
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions final">
          <button type="submit" disabled={creating} className="btn-primary">
            {creating ? 'Creating Story…' : 'Create Story'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/artSpeaks')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoryCreationPage;

